import { useMemo, useCallback } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ShoppingCartIcon, UserCircleIcon } from "@heroicons/react/20/solid";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { logout } from "../features/auth/Auth";
import { store } from "../store";
import { clearCart } from "../features/cart/cartSlice";
import CardProduct from "./product/CardProduct";
import { toast } from "react-toastify";
import { decrypt } from "../utils/CryptData";
import { logoMoide, emptyCart } from "../assets/images";

const classNames = (...classes) => classes.filter(Boolean).join(" ");

const BASE_NAVIGATIONS = [
  { name: "Accueil", href: "/" },
  { name: "Services", href: "/product" },
  { name: "À propos", href: "/about" },
  { name: "Contact", href: "/contact" },
];

export default function Header() {
  const { pathname } = useLocation();
  const { cartItems, total } = useSelector((state) => state.cartProduct);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    const storedData = localStorage.getItem("dus");
    return storedData ? JSON.parse(decrypt(storedData)) : null;
  }, []);

  const navigations = useMemo(() => {
    if (currentUser?.user[0]?.role === "admin") {
      return [...BASE_NAVIGATIONS, { name: "Dashboard", href: "/dashboard" }];
    }
    return BASE_NAVIGATIONS;
  }, [currentUser]);

  const handleLogout = useCallback(() => {
    store.dispatch(logout());
    store.dispatch(clearCart());
    navigate("/");
  }, [navigate]);

  const handleOrder = useCallback(() => {
    if (isLoggedIn) {
      navigate("/order");
    } else {
      toast.error("Vous devez être connecté pour passer une commande");
    }
  }, [isLoggedIn, navigate]);
  return (
    <Disclosure as="nav" className="bg-primary sticky top-0 z-50 ">
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div className="relative flex h-16 items-center justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                {/* Mobile menu button*/}
                <DisclosureButton className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-center sm:justify-start gap-8">
                <Link to="/" className="flex-shrink-0">
                  <img
                    src={logoMoide}
                    alt="Moide Ferme"
                    className="h-12 w-11"
                  />
                </Link>
                <div className="hidden sm:flex sm:items-center">
                  <div className="flex items-center space-x-4">
                    {navigations.map((item) => (
                      <Link key={item.name} to={item.href}>
                        <span
                          className={classNames(
                            item.href === pathname
                              ? "bg-gray-200 text-gray-800"
                              : "text-gray-300 hover:bg-gray-200 hover:text-gray-800",
                            "rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap"
                          )}
                          aria-current={item.current ? "page" : undefined}
                        >
                          {item.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:space-x-2">
                {!isLoggedIn ? (
                  <>
                    <Link
                      to="/login"
                      className="text-gray-300 hover:bg-white hover:text-gray-800 rounded-md px-3 py-2"
                    >
                      Se connecter
                    </Link>
                    <span className="h-6 w-px bg-gray-200" aria-hidden="true" />
                    <Link
                      to="/sign_up"
                      className="text-gray-300 hover:bg-white hover:text-gray-800 rounded-md px-3 py-2"
                    >
                      Créer un compte
                    </Link>
                  </>
                ) : null}
              </div>

              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                {/* <button
                  className="text-accent-content btn btn-ghost btn-circle text-xl"
                  onClick={() => dispatch(changeMode())}
                >
                  {darkMode ? (
                    <SunIcon className="h-6 w-6" aria-hidden="true" color="gray" />
                  ) : (
                    <MoonIcon className="h-6 w-6" aria-hidden="true" color="gray" />
                  )}
                </button> */}
                <div className="dropdown dropdown-end">
                  <label tabIndex={0} className="btn btn-ghost btn-circle ">
                    <div className="indicator">
                      {cartItems?.length > 0 && (
                        <span className="indicator-item badge badge-secondary h-5 w-5">
                          {cartItems?.length}
                        </span>
                      )}

                      <ShoppingCartIcon
                        className="h-6 w-6  text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                  </label>
                  {/* Basket */}
                  <div
                    tabIndex={0}
                    className="mt-3  card card-compact dropdown-content w-96  shadow bg-white"
                  >
                    <div className="card-body ">
                      {cartItems?.length ? (
                        <>
                          {cartItems && <CardProduct product={cartItems} />}
                          <div className="border-b pb-3 text-xl text-gray-700">
                            Total: {total} Fcfa
                          </div>

                          <div className="card-actions">
                            <button
                              onClick={handleOrder}
                              className="btn text-lg bg-black btn-block text-white hover:bg-green-500 text-base-content"
                            >
                              Passez la commande
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center">
                          <img
                            src={emptyCart}
                            alt="Panier vide"
                            className="w-1/5 mx-auto"
                          />
                          <p className="text-lg my-3">Aucun produit ajouté</p>{" "}
                        </div>
                      )}
                    </div>
                  </div>
                  {/*End Basket */}
                </div>
                {isLoggedIn && (
                  <Menu as="div" className="relative ml-3">
                    <div>
                      <MenuButton className="relative flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                        <span className="absolute -inset-1.5" />
                        <span className="sr-only">Open user menu</span>

                        <UserCircleIcon
                          className="h-8 w-8  text-gray-400"
                          aria-hidden="true"
                        />
                      </MenuButton>
                    </div>
                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1  ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <MenuItem>
                          {({ focus }) => (
                            <Link
                              to="/profile"
                              className={classNames(
                                focus ? "bg-gray-100" : "",
                                "block px-4 py-2 text-md text-gray-700 flex items-center gap-2"
                              )}
                            >
                              <svg
                                className="h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                              >
                                <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z" />
                              </svg>
                              Profile
                            </Link>
                          )}
                        </MenuItem>
                        <MenuItem className="my-2">
                          {({ focus }) => (
                            <Link
                              to="/inventory"
                              className={classNames(
                                focus ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700 flex items-center gap-2"
                              )}
                            >
                              <svg
                                className="h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 576 512"
                              >
                                <path d="M24 0C10.7 0 0 10.7 0 24S10.7 48 24 48l45.5 0c3.8 0 7.1 2.7 7.9 6.5l51.6 271c6.5 34 36.2 58.5 70.7 58.5L488 384c13.3 0 24-10.7 24-24s-10.7-24-24-24l-288.3 0c-11.5 0-21.4-8.2-23.6-19.5L170.7 288l288.5 0c32.6 0 61.1-21.8 69.5-53.3l41-152.3C576.6 57 557.4 32 531.1 32L360 32l0 102.1 23-23c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-64 64c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l23 23L312 32 120.1 32C111 12.8 91.6 0 69.5 0L24 0zM176 512a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm336-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0z" />
                              </svg>
                              Mes commandes
                            </Link>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <Link
                              onClick={() => handleLogout()}
                              to="#"
                              className={classNames(
                                focus ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700 flex items-center gap-2"
                              )}
                            >
                              <svg
                                className="h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                              >
                                <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 192 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l210.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128zM160 96c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 32C43 32 0 75 0 128L0 384c0 53 43 96 96 96l64 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-64 0c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l64 0z" />
                              </svg>
                              Se déconnecter
                            </Link>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                )}
              </div>
            </div>
          </div>

          <DisclosurePanel className="sm:hidden">
            <div className="space-y-1 px-3  z-20 border-b-2 border-gray-100">
              {navigations.map((item) => (
                <Link key={item.name} className="w-full" to={item.href}>
                  <span
                    className={classNames(
                      item.href === pathname
                        ? "bg-gray-200 text-gray-800 "
                        : "text-gray-300 hover:bg-gray-200 hover:text-gray-800",
                      "rounded-md px-3 py-2 text-sm font-medium"
                    )}
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </span>
                </Link>
              ))}

              {!isLoggedIn && (
                <div className="space-y-6 border-t border-gray-200 px-3 py-6">
                  <div className="flow-root">
                    <Link
                      to="/login"
                      className="-m-2 block p-2 text-gray-300 hover:bg-white hover:text-gray-800 rounded-md"
                    >
                      Se connecter
                    </Link>
                  </div>
                  <div className="flow-root">
                    <Link
                      to="/sign_up"
                      className="-m-2 block p-2 text-gray-300 hover:bg-white hover:text-gray-800 rounded-md"
                    >
                      S'inscrire
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
